import { useState } from "react";
import { contractTemplates } from "../../../mocks/legal";

export default function ContractDrafter() {
  const [selectedTemplate, setSelectedTemplate] = useState(contractTemplates[0].id);
  const [editedClauses, setEditedClauses] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(contractTemplates.map((t) => [t.id, [...t.clauses]]))
  );
  const [newClause, setNewClause] = useState("");
  const [savedMsg, setSavedMsg] = useState(false);

  const currentTemplate = contractTemplates.find((t) => t.id === selectedTemplate)!;
  const currentClauses = editedClauses[selectedTemplate] || [];

  const handleClauseChange = (idx: number, value: string) => {
    setEditedClauses((prev) => ({
      ...prev,
      [selectedTemplate]: prev[selectedTemplate].map((c, i) => (i === idx ? value : c)),
    }));
  };

  const handleAddClause = () => {
    if (!newClause.trim()) return;
    setEditedClauses((prev) => ({
      ...prev,
      [selectedTemplate]: [...prev[selectedTemplate], newClause],
    }));
    setNewClause("");
  };

  const handleDeleteClause = (idx: number) => {
    setEditedClauses((prev) => ({
      ...prev,
      [selectedTemplate]: prev[selectedTemplate].filter((_, i) => i !== idx),
    }));
  };

  const handleSave = () => {
    setSavedMsg(true);
    setTimeout(() => setSavedMsg(false), 2500);
  };

  return (
    <div className="space-y-4">
      {savedMsg && (
        <div className="bg-[#00C896]/10 border border-[#00C896]/30 rounded-xl px-4 py-3 text-[#00C896] text-sm flex items-center gap-2">
          <i className="ri-check-double-line text-lg"></i>계약서가 저장되었습니다.
        </div>
      )}

      {/* Template Selector */}
      <div className="flex items-center gap-2 flex-wrap">
        {contractTemplates.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setSelectedTemplate(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm transition-all cursor-pointer whitespace-nowrap ${
              selectedTemplate === t.id
                ? "border-[#E8375F] bg-[#E8375F]/10 text-[#E8375F] font-semibold"
                : "border-[#2A2D3E] text-[#8B91A8] hover:border-[#3E4155] hover:text-[#555555]"
            }`}
          >
            <i className={`${selectedTemplate === t.id ? "ri-file-list-3-fill text-[#E8375F]" : "ri-file-list-3-line"} text-sm`}></i>
            {t.title}
          </button>
        ))}
      </div>

      {/* Editor */}
      <div className="bg-[#1C1F2E] rounded-xl border border-[#2A2D3E] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#252830] flex items-center justify-between">
          <div>
            <div className="text-[#111111] font-bold">{currentTemplate.title}</div>
            <div className="text-[#5A5E72] text-xs mt-0.5">최종 수정: {currentTemplate.lastModified} · 조항 {currentClauses.length}개</div>
          </div>
          <button
            type="button"
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#E8375F] to-[#FF4B7A] text-white text-sm font-semibold rounded-lg cursor-pointer hover:shadow-lg whitespace-nowrap"
          >
            <i className="ri-save-line text-sm"></i>저장
          </button>
        </div>

        {/* Clauses */}
        <div className="p-5 space-y-3">
          {currentClauses.map((clause, idx) => (
            <div key={idx} className="flex items-start gap-3 group">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#252830] text-[#5A5E72] text-[10px] font-bold shrink-0 mt-2.5">
                {idx + 1}
              </div>
              <textarea
                value={clause}
                onChange={(e) => handleClauseChange(idx, e.target.value)}
                rows={2}
                className="flex-1 bg-[#252830] text-white text-sm px-3 py-2.5 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#E8375F] resize-none leading-relaxed"
              />
              <button
                type="button"
                onClick={() => handleDeleteClause(idx)}
                className="w-7 h-7 flex items-center justify-center text-[#3E4155] hover:text-[#E8375F] transition-colors opacity-0 group-hover:opacity-100 cursor-pointer mt-2.5"
              >
                <i className="ri-delete-bin-line text-sm"></i>
              </button>
            </div>
          ))}

          {/* Add Clause */}
          <div className="flex items-center gap-2 mt-2 pt-3 border-t border-[#252830]">
            <input
              value={newClause}
              onChange={(e) => setNewClause(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddClause()}
              placeholder="새 조항 내용을 입력하세요..."
              className="flex-1 bg-[#252830] text-white text-sm px-3 py-2.5 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#E8375F] placeholder:text-[#3E4155]"
            />
            <button
              type="button"
              onClick={handleAddClause}
              className="px-4 py-2.5 bg-[#E8375F] text-white text-sm rounded-lg hover:bg-[#FF4B7A] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-1"></i>추가
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
